import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from "../../../app/store";
import {
    BotInListDTO,
    BotType,
    CcRecipientDTO,
    FullBotUi,
    LocaleTag,
    SupportedTimezone,
    UpdateBotRequest
} from "../../../api/generated";
import {botApi, RequestStatus} from "../../../api/apiConfiguration";
import {selectExceptionally} from "../../../utils/selectExceptionally";
import {removeTrailingSlash} from "../../../utils/urlUtils";
import {DEFAULT_SUPPORTED_PHONE_NUMBER_LOCALES} from "./phoneLocaleUtil";
import {DEFAULT_SURVEY_MESSAGE_COUNT, normalizeSurveyMessageCount} from "./surveySettings";
import {FranchiseRequestUi} from "./franchiseTypes";

export const fetchBotToUpdate = createAsyncThunk(
    'botUpdate/fetchBotById',
    (publicId: string) => botApi.getBotById({publicId})
);

export const updateBot = createAsyncThunk<BotInListDTO, undefined, { state: RootState }>(
    "botUpdate/update",
    async (_payload, {getState, rejectWithValue}) => {
        const {botUpdate} = getState();
        const {updateBotRequest} = botUpdate;
        const request = mapRequest(updateBotRequest);
        try {
            return await botApi.updateBot({updateBotRequest: request});
        } catch (error: any) {
            const message = error?.response ? await error.response.text() : null;
            return rejectWithValue(message);
        }
    }
);

export type UpdateBotRequestUI =
    Omit<UpdateBotRequest, "emailTranscriptRecipients" | "publicFranchiseIdList" | "ccRecipients">
    & {
    emailTranscriptRecipients: string,
    ccRecipients: CcRecipientDTO[],
    franchiseList: FranchiseRequestUi[],
    useMultiAgent: boolean,
    externalCommunicationsDisabled: boolean
}
export type UpdateBotRequestPartial = Partial<UpdateBotRequestUI>

export interface UpdateBotState {
    updateBotRequest?: UpdateBotRequestPartial
    originalBotType?: BotType
    initialBotLoadingStatus: RequestStatus
    updateBotRequestStatus: RequestStatus
    errorMessage: string | null
}

const buildInitialState = (): UpdateBotState => {
    return {
        initialBotLoadingStatus: RequestStatus.NONE,
        updateBotRequestStatus: RequestStatus.NONE,
        originalBotType: undefined,
        errorMessage: null
    }
}

const initialState: UpdateBotState = buildInitialState()

export const botUpdateSlice = createSlice({
    name: 'botUpdate',
    initialState,
    reducers: {
        clearUpdateBotState: () => {
            return buildInitialState();
        },
        updateUpdateBotRequest: (state, {payload}: PayloadAction<UpdateBotRequestPartial>) => {
            state.updateBotRequest = {
                ...state.updateBotRequest,
                ...payload
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchBotToUpdate.pending, (state) => {
            state.initialBotLoadingStatus = RequestStatus.PENDING;
        })
        builder.addCase(fetchBotToUpdate.fulfilled, (state, {payload}) => {
            state.initialBotLoadingStatus = RequestStatus.RESOLVED;
            state.originalBotType = payload.botType;
            state.updateBotRequest = assignFields(payload);
        })
        builder.addCase(fetchBotToUpdate.rejected, (state) => {
            state.initialBotLoadingStatus = RequestStatus.REJECTED;
        })
        builder.addCase(updateBot.pending, (state) => {
            state.updateBotRequestStatus = RequestStatus.PENDING;
            state.errorMessage = null;
        })
        builder.addCase(updateBot.fulfilled, (state) => {
            state.updateBotRequestStatus = RequestStatus.RESOLVED;
        })
        builder.addCase(updateBot.rejected, (state, action) => {
            state.updateBotRequestStatus = RequestStatus.REJECTED;
            state.errorMessage = (action.payload as string | null) ?? null;
        })
    },
});

export const prepareEmails = (emailsInString?: string): Array<string> => {
    if (emailsInString) {
        return Array.from(new Set(selectExceptionally(emailsInString).split(";").map(e => e.trim())))
    }
    return [] as Array<string>
}

const mapFranchisePublicIdList = (franchiseList?: FranchiseRequestUi[]): string[] => {
    return (franchiseList || []).map(franchise => franchise.publicId);
}

const hasPublicId = (franchise: any): franchise is any & { publicId: string } => !!franchise.publicId;

const prepareCcRecipients = (recipients?: CcRecipientDTO[]): CcRecipientDTO[] => {
    const recipientsByEmail = new Map<string, CcRecipientDTO>();
    (recipients || [])
        .map(recipient => ({
            title: recipient.title?.trim() || "",
            email: recipient.email?.trim() || ""
        }))
        .filter(recipient => recipient.email)
        .forEach(recipient => {
            const key = recipient.email!.toLowerCase();
            if (!recipientsByEmail.has(key)) {
                recipientsByEmail.set(key, recipient);
            }
        });
    return Array.from(recipientsByEmail.values());
}

const prepareOptionalText = (value?: string): string | undefined => {
    const normalized = value?.trim();
    return normalized || undefined;
}

const assignFields = (botUi: FullBotUi): UpdateBotRequestPartial => {
    const franchiseList: FranchiseRequestUi[] = ((botUi as FullBotUi & { franchiseList?: any[] }).franchiseList || [])
        .filter(hasPublicId)
        .map((franchise): FranchiseRequestUi => ({
            id: franchise.id,
            publicId: franchise.publicId,
            name: franchise.name || "",
            franchiseHash: franchise.franchiseHash,
            urlPathPattern: franchise.urlPathPattern,
            focusEnabled: franchise.focusEnabled ?? true,
            sharedWithFocusedFranchises: Boolean(franchise.sharedWithFocusedFranchises),
            franchiseEmailConfigurationId: franchise.franchiseEmailConfigurationId,
            franchiseEmailTranscriptRecipients: (franchise.franchiseEmailTranscriptRecipients || []).join(";"),
            franchiseEmailFiltrationEnabled: Boolean(franchise.franchiseEmailFiltrationEnabled),
            franchiseEmailTranscriptSummaryEnabled: Boolean(franchise.franchiseEmailTranscriptSummaryEnabled),
            publicLocationIdList: (franchise.locationList || []).map((location: any) => ({
                id: location.id,
                publicId: location.publicId,
                locationName: location.name,
                vehicleDataProvider: location.vehicleDataProvider,
                locationHash: location.locationHash
            })),
            newVehicleDataConfiguration: franchise.newVehicleDataConfiguration,
            knowledgeRecords: franchise.knowledgeRecords || []
        }));

    return {
        id: botUi.id,
        emailConfigurationId: botUi.emailConfigurationId,
        franchiseList,
        publicId: botUi.publicId,
        botType: botUi.botType,
        useMultiAgent: (botUi as FullBotUi & { useMultiAgent?: boolean }).useMultiAgent ?? false,
        vehicleProviderWarningEmailEnabled: (botUi as FullBotUi & { vehicleProviderWarningEmailEnabled?: boolean }).vehicleProviderWarningEmailEnabled ?? true,
        externalCommunicationsDisabled: (botUi as FullBotUi & { externalCommunicationsDisabled?: boolean }).externalCommunicationsDisabled ?? false,
        siteName: botUi.siteName,
        siteURL: botUi.siteURL,
        groupHash: botUi.groupHash,
        conversationLimit: botUi.conversationLimit,
        workingRange: botUi.workingRange,
        teamInfo: botUi.teamInfo,
        openAiApiKey: botUi.openAiApiKey,
        openAiModel: botUi.openAiModel,
        assistantEmail: botUi.assistantEmail,
        assistantEmailPassword: botUi.assistantEmailPassword,
        emailTranscriptRecipients: botUi.emailTranscriptRecipients.join(";"),
        lastRenewalDate: botUi.lastRenewalDate,
        filtrationEnabled: botUi.filtrationEnabled,
        transcriptSummaryEnabled: botUi.transcriptSummaryEnabled,
        sharingLinkEnabled: botUi.sharingLinkEnabled ?? false,
        surveyAfterMessagesEnabled: botUi.surveyAfterMessagesEnabled ?? false,
        surveyAfterMessagesCount: botUi.surveyAfterMessagesCount ?? DEFAULT_SURVEY_MESSAGE_COUNT,
        surveyAfterBookingEnabled: botUi.surveyAfterBookingEnabled ?? false,
        redirectProvider: botUi.redirectProvider,
        urlProcessorConfigurationId: botUi.urlProcessorConfigurationId,
        urlProcessorEnabled: botUi.urlProcessorEnabled,
        matchField: botUi.matchField,
        matchType: botUi.matchType,
        segmentNumberFromTheEnd: botUi.segmentNumberFromTheEnd,
        timezone: botUi.timezone ?? SupportedTimezone.EuropeLondon,
        defaultLocale: botUi.defaultLocale ?? LocaleTag.EnGb,
        supportedPhoneNumberLocales: botUi.supportedPhoneNumberLocales?.length
            ? botUi.supportedPhoneNumberLocales
            : DEFAULT_SUPPORTED_PHONE_NUMBER_LOCALES,
        ccRecipients: botUi.ccRecipients || [],
    }
}

const mapRequest = (request?: UpdateBotRequestPartial): UpdateBotRequest => {
    if (request) {
        return {
            id: selectExceptionally(request.id),
            emailConfigurationId: selectExceptionally(request.emailConfigurationId),
            publicFranchiseIdList: mapFranchisePublicIdList(request.franchiseList),
            urlProcessorConfigurationId: selectExceptionally(request.urlProcessorConfigurationId),

            publicId: selectExceptionally(request.publicId),
            botType: selectExceptionally(request.botType),
            useMultiAgent: request.useMultiAgent ?? false,
            vehicleProviderWarningEmailEnabled: request.vehicleProviderWarningEmailEnabled ?? true,
            externalCommunicationsDisabled: request.externalCommunicationsDisabled ?? false,
            siteName: selectExceptionally(request.siteName),
            siteURL: removeTrailingSlash(selectExceptionally(request.siteURL)),
            groupHash: request.groupHash,
            conversationLimit: selectExceptionally(request.conversationLimit),
            workingRange: request.workingRange,
            teamInfo: request.teamInfo,
            openAiApiKey: request.openAiApiKey,
            openAiModel: prepareOptionalText(request.openAiModel),
            assistantEmail: request.assistantEmail || "",
            assistantEmailPassword: request.assistantEmailPassword || "",
            emailTranscriptRecipients: prepareEmails(request.emailTranscriptRecipients),
            lastRenewalDate: selectExceptionally(request.lastRenewalDate),
            filtrationEnabled: selectExceptionally(request.filtrationEnabled),
            transcriptSummaryEnabled: selectExceptionally(request.transcriptSummaryEnabled),
            sharingLinkEnabled: selectExceptionally(request.sharingLinkEnabled),
            surveyAfterMessagesEnabled: request.surveyAfterMessagesEnabled ?? false,
            surveyAfterMessagesCount: normalizeSurveyMessageCount(request.surveyAfterMessagesCount),
            surveyAfterBookingEnabled: request.surveyAfterBookingEnabled ?? false,

            redirectProvider: selectExceptionally(request.redirectProvider),
            urlProcessorEnabled: selectExceptionally(request.urlProcessorEnabled),
            matchField: request.matchField,
            matchType: request.matchType,
            segmentNumberFromTheEnd: request.segmentNumberFromTheEnd,
            timezone: request.timezone,
            defaultLocale: request.defaultLocale,
            supportedPhoneNumberLocales: request.supportedPhoneNumberLocales ?? DEFAULT_SUPPORTED_PHONE_NUMBER_LOCALES,
            ccRecipients: prepareCcRecipients(request.ccRecipients),
        } as UpdateBotRequest & { externalCommunicationsDisabled: boolean }
    } else {
        throw Error("updateRequest is Undefined")
    }
}

export const {
    clearUpdateBotState,
    updateUpdateBotRequest
} = botUpdateSlice.actions;

export const updateBotRequestSelector = (state: RootState): UpdateBotRequestPartial => selectExceptionally(state.botUpdate.updateBotRequest);
export const originalBotTypeSelector = (state: RootState): BotType | undefined => state.botUpdate.originalBotType;
export const updateBotRequestStatusSelector = (state: RootState): RequestStatus => state.botUpdate.updateBotRequestStatus;
export const initialBotLoadingStatusSelector = (state: RootState): RequestStatus => state.botUpdate.initialBotLoadingStatus;
export const updateBotErrorMessageSelector = (state: RootState): string | null => state.botUpdate.errorMessage;
